import { useEffect, useRef } from 'react';

/**
 * While `active`, Escape goes to `handler` and nothing else sees it.
 *
 * The task window closes on Escape from a CAPTURE listener on `document`
 * (TaskDetailModal), so an input inside it cannot keep Escape with a plain
 * stopPropagation. A capture listener on `window` runs before that one, so an
 * open inline edit (a subtask rename, a typed-but-unsent new subtask) takes
 * Escape for itself — the same rule the editor's "/" and "@" menus follow.
 * `handler` may return `false` to let Escape through after all.
 */
export default function useEscapeFirst(active, handler) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (handlerRef.current?.(e) === false) return;
      e.preventDefault();
      e.stopImmediatePropagation();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [active]);
}
